import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import './InspectionInitiated.scss';

const normalizeTime = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{2}:\d{2}/.test(raw)) return raw.substring(0, 5);
  return raw;
};

export default function InspectionInitiated() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [inspection, setInspection] = useState(null);
  const [quotation, setQuotation] = useState(location.state?.quotation ?? null);
  const [p2pPayment, setP2pPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInspectionData = useCallback(async () => {
    try {
      setLoading(true);

      if (location.state?.quotation) {
        setQuotation(location.state.quotation);
      }

      const response = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_BY_AUCTION(auctionId));
      if (response?.success && response.data) {
        setInspection(response.data);
        setP2pPayment(response.data.p2p_payment ?? null);
        if (!location.state?.quotation) {
          setQuotation({
            inspector_name: response.data.inspector_name,
            inspector_company: response.data.inspector_company,
            quote_amount: response.data.inspection_fee,
            proposed_date: response.data.inspection_date,
            proposed_time: response.data.inspection_time,
          });
        }
        return;
      }

      if (!location.state?.quotation) {
        const rfqResponse = await CommonService.get(
          API_URLS.AUCTION.GET_INSPECTION_RFQ_BY_AUCTION(auctionId),
        );
        if (rfqResponse?.success && rfqResponse.data?.rfq_id) {
          const quotesResponse = await CommonService.get(
            API_URLS.AUCTION.GET_INSPECTION_QUOTATIONS(rfqResponse.data.rfq_id),
          );
          if (quotesResponse?.success && Array.isArray(quotesResponse.data)) {
            const selected =
              quotesResponse.data.find((q) => q.status === 'selected') ||
              quotesResponse.data[0];
            if (selected) setQuotation(selected);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching inspection:', error);
    } finally {
      setLoading(false);
    }
  }, [auctionId, location.state?.quotation]);

  useEffect(() => {
    fetchInspectionData();
    const onFocus = () => fetchInspectionData();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchInspectionData]);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusLabel = (status) => {
    const s = (status || '').toLowerCase();
    const map = {
      scheduled: 'Scheduled',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return map[s] || status || '—';
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    const map = {
      scheduled: 'status-scheduled',
      in_progress: 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return map[s] || '';
  };

  if (loading) {
    return <div className="inspection-initiated loading">Loading...</div>;
  }

  const inspectionStatus = inspection?.status || (quotation ? 'scheduled' : null);
  const inspectorName =
    inspection?.inspector_name || quotation?.inspector_name || 'Inspector Name';
  const inspectorCompany =
    inspection?.inspector_company || quotation?.inspector_company || 'Company Name';
  const inspectionFee =
    inspection?.payable_amount ??
    inspection?.inspection_fee ??
    quotation?.quote_amount ??
    0;
  const inspectionDate =
    inspection?.inspection_date ||
    quotation?.proposed_date ||
    quotation?.proposed_start_date;
  const inspectionTime = normalizeTime(
    inspection?.inspection_time || quotation?.proposed_time || '10:00',
  );

  return (
    <div className="inspection-initiated">
      <div className="success-container">
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#28a745"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        <h1 className="success-title">✓ Inspection Initiated!</h1>
        <p className="success-description">
          {inspectorName} from {inspectorCompany} has been notified to begin the inspection.
        </p>

        <div className="inspection-details-box">
          <div className="detail-item">
            <span className="detail-label">Inspection Status</span>
            <span className={`inspection-status-badge ${getStatusClass(inspectionStatus)}`}>
              {getStatusLabel(inspectionStatus)}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Inspector</span>
            <div className="detail-value-group">
              <span className="detail-value-bold">{inspectorName}</span>
              <span className="detail-value-sub">{inspectorCompany}</span>
            </div>
          </div>
          <div className="detail-item">
            <span className="detail-label">Inspection Fee</span>
            <span className="detail-value-bold orange">{formatCurrency(inspectionFee)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Scheduled Date</span>
            <span className="detail-value">{formatDate(inspectionDate) || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Time</span>
            <span className="detail-value">{inspectionTime || '—'}</span>
          </div>
        </div>

        <div className="info-alert">
          <div className="alert-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="2"/>
              <polyline points="10 4 10 10 13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="alert-content">
            <p>
              The inspector will complete the inspection within <strong>2-3 hours</strong> and submit a detailed report.
              You will be notified via email and SMS.
            </p>
          </div>
        </div>

        <div className="action-buttons">
          {p2pPayment?.can_show_payment_now && inspection?.inspection_id ? (
            <button
              className="btn-primary"
              type="button"
              onClick={() =>
                navigate(`/qa/inspection/${inspection.inspection_id}/payment`, {
                  state: {
                    fromInspectionInitiated: true,
                    auctionId,
                    totalQuote: inspectionFee,
                    currency: inspection?.currency || 'INR',
                  },
                })
              }
            >
              Payment Now
            </button>
          ) : null}
          {p2pPayment?.payment_approval_pending ? (
            <p className="inspection-payment-approval-hint">
              Payment approval is in progress. Payment Now will appear when all approval levels are complete.
            </p>
          ) : null}
          {p2pPayment?.is_p2p_linked && !p2pPayment?.job_accepted_at ? (
            <p className="inspection-payment-approval-hint">
              Waiting for the inspector to accept the assignment before payment is available.
            </p>
          ) : null}
          {p2pPayment?.buyer_payment_status === 'paid' ? (
            <p className="inspection-payment-approval-hint">
              Inspection fee payment completed.
            </p>
          ) : null}
          <button className="btn-primary" onClick={() => navigate(`/auction/${auctionId}`)}>
            View Auction Details
          </button>
          <button className="btn-secondary" onClick={() => navigate('/auction/my/bids')}>
            Go to My Bids
          </button>
        </div>
      </div>
    </div>
  );
}
